/**
 * CheckoutRenderer - Renderiza o checkout baseado na configuração do Builder
 * Este componente lê o novo formato JSON e renderiza os componentes dinamicamente
 */

import React from 'react';
import { CheckoutBuilderData, CheckoutNode } from '../../types/checkoutConfig';
import CheckoutText from './checkout-components/CheckoutText';
import CheckoutImage from './checkout-components/CheckoutImage';
import CheckoutTimer from './checkout-components/CheckoutTimer';

interface CheckoutRendererProps {
    builderConfig: CheckoutBuilderData | any; // Aceitar any para simplificar
    isMobile?: boolean;
    renderOnlyRows?: string[]; // Opcional: renderizar apenas linhas específicas
}

interface RenderNodeProps {
    nodeId: string;
    nodes: Record<string, CheckoutNode>;
    isMobile: boolean;
}

/**
 * Mapeamento de tipos de componentes para seus componentes React
 */
const COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
    'texto': CheckoutText,
    'imagem': CheckoutImage,
    'cronometro': CheckoutTimer,
};

/**
 * Renderiza um componente individual baseado no tipo
 */
const RenderComponent: React.FC<{
    node: CheckoutNode;
}> = ({ node }) => {
    if (node.type !== 'component') return null;

    const Component = COMPONENT_MAP[node.compType];
    if (!Component) {
        console.warn(`Componente não encontrado: ${node.compType}`);
        return null;
    }

    // Adaptar props para o formato esperado pelos componentes
    // No modo de visualização (não edição), não precisamos de callbacks
    const adaptedProps = {
        selected: false,
        config: node.props,
        onSelect: () => { }, // No-op no modo visualização
        onConfigChange: undefined,
        onDelete: undefined,
        onDuplicate: undefined,
        dragHandleProps: undefined,
        viewMode: true, // Modo visualização (não editável)
    };

    return (
        <div className="checkout-component pointer-events-none w-full">
            <Component {...adaptedProps} />
        </div>
    );
};

/**
 * Renderiza uma coluna com seus componentes filhos
 */
const RenderColumn: React.FC<RenderNodeProps> = ({ nodeId, nodes, isMobile }) => {
    const columnNode = nodes[nodeId];
    if (!columnNode || columnNode.type !== 'column') return null;

    const { widthByBp, align = 'start', visibilityByBp } = columnNode.props;

    // Verificar visibilidade baseada no breakpoint
    const breakpoint = isMobile ? 'mobile' : 'desktop';
    if (visibilityByBp && visibilityByBp[breakpoint] === false) {
        return null;
    }

    // Calcular classes de largura baseado no grid de 12 colunas
    const width = isMobile ? widthByBp.mobile : widthByBp.desktop;
    const widthClass = `col-span-${width}`;

    // Classes de alinhamento
    const alignClass = {
        'start': 'items-start',
        'center': 'items-center',
        'end': 'items-end',
        'stretch': 'items-stretch'
    }[align];

    // Reduzir o espaço vertical entre componentes empilhados (especialmente na lateral)
    const gapClass = 'gap-1';

    return (
        <div className={`${widthClass} flex flex-col ${alignClass} ${gapClass}`}>
            {columnNode.children.map(childId => {
                const childNode = nodes[childId];
                if (!childNode) return null;

                return (
                    <RenderComponent
                        key={childId}
                        node={childNode}
                    />
                );
            })}
        </div>
    );
};

/**
 * Renderiza uma linha com suas colunas filhas
 */
const RenderRow: React.FC<RenderNodeProps> = ({ nodeId, nodes, isMobile }) => {
    const rowNode = nodes[nodeId];
    if (!rowNode || rowNode.type !== 'row') return null;

    const { gap = 4, marginY = 0, visibilityByBp } = rowNode.props;

    // Verificar visibilidade baseada no breakpoint
    const breakpoint = isMobile ? 'mobile' : 'desktop';
    if (visibilityByBp && visibilityByBp[breakpoint] === false) {
        return null;
    }

    // Linha sem nenhum componente visível não deve ocupar espaço (nem gerar gap entre linhas)
    const hasContent = rowNode.children.some(colId => {
        const col = nodes[colId];
        if (!col || col.type !== 'column') return false;
        if (col.props?.visibilityByBp && col.props.visibilityByBp[breakpoint] === false) return false;
        return col.children.some(childId => nodes[childId]?.type === 'component');
    });
    if (!hasContent) return null;

    const gapClass = `gap-${gap}`;
    const marginClass = marginY > 0 ? `my-${marginY}` : '';

    return (
        <div className={`grid grid-cols-12 ${gapClass} ${marginClass} w-full`}>
            {rowNode.children.map(childId => (
                <RenderColumn
                    key={childId}
                    nodeId={childId}
                    nodes={nodes}
                    isMobile={isMobile}
                />
            ))}
        </div>
    );
};

/**
 * Componente principal que renderiza o checkout
 */
const CheckoutRenderer: React.FC<CheckoutRendererProps> = ({ builderConfig, isMobile = false, renderOnlyRows }) => {
    const desktopConfig = builderConfig.configurations?.desktop;
    const mobileConfig = builderConfig.configurations?.mobile;

    const hasMobileConfig = !!(
        mobileConfig &&
        mobileConfig.document &&
        mobileConfig.document.nodes &&
        Object.keys(mobileConfig.document.nodes).length > 0
    );

    // Se estiver em mobile mas não houver configuração mobile, usar desktop como fallback
    const effectiveIsMobile = isMobile && hasMobileConfig;
    const config = effectiveIsMobile ? mobileConfig : desktopConfig;
    if (!config) return null;

    const { document, globalSettings } = config;
    if (!document || !document.nodes) return null;

    // Em alguns casos, nós mobile (linhas/colunas) podem referenciar componentes
    // salvos apenas no documento desktop. Para garantir que tudo apareça no mobile,
    // fazemos um merge dos nodes desktop + mobile quando effectiveIsMobile === true.
    const baseNodes = document.nodes || {};
    const mergedNodes = effectiveIsMobile && desktopConfig?.document?.nodes
        ? { ...desktopConfig.document.nodes, ...baseNodes }
        : baseNodes;

    // Aplicar configurações globais
    const containerStyle: React.CSSProperties = {
        fontFamily: globalSettings?.fontFamily || 'inherit',
        backgroundColor: globalSettings?.backgroundColor || 'transparent',
    };

    if (globalSettings?.backgroundImage) {
        containerStyle.backgroundImage = `url(${globalSettings.backgroundImage})`;
        containerStyle.backgroundAttachment = globalSettings.fixedBackground ? 'fixed' : 'scroll';
        containerStyle.backgroundRepeat = globalSettings.repeatBackground ? 'repeat' : 'no-repeat';
        containerStyle.backgroundSize = globalSettings.expandBackground ? 'cover' : 'auto';
    }

    // Se renderOnlyRows foi especificado, renderizar apenas essas linhas
    if (renderOnlyRows && renderOnlyRows.length > 0) {
        return (
            <div className="checkout-renderer w-full" style={containerStyle}>
                <div className="flex flex-col gap-8 w-full">
                    {renderOnlyRows.map((rowId: string) => (
                        <RenderRow
                            key={rowId}
                            nodeId={rowId}
                            nodes={mergedNodes}
                            isMobile={effectiveIsMobile}
                        />
                    ))}
                </div>
            </div>
        );
    }

    // Mobile: renderizar mobileRoot se existir, senão usar root como fallback
    if (effectiveIsMobile) {
        const mobileRows = (document.mobileRoot && document.mobileRoot.length > 0)
            ? document.mobileRoot
            : (document.root || []);

        return (
            <div className="checkout-renderer w-full" style={containerStyle}>
                <div className="flex flex-col gap-3 w-full">
                    {mobileRows.map((rowId: string) => (
                        <RenderRow
                            key={rowId}
                            nodeId={rowId}
                            nodes={mergedNodes}
                            isMobile={true}
                        />
                    ))}
                </div>
            </div>
        );
    }

    // Desktop: renderizar root
    const topRows = document.root || [];

    return (
        <div className="checkout-renderer w-full" style={containerStyle}>
            <div className="flex flex-col gap-4 w-full">
                {topRows.map((rowId: string) => (
                    <RenderRow
                        key={rowId}
                        nodeId={rowId}
                        nodes={mergedNodes}
                        isMobile={false}
                    />
                ))}
            </div>
        </div>
    );
};

export default CheckoutRenderer;
